/* eslint-disable no-console */
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  TableContainer,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import PennantTeams from "./PennantTeams";

// Utility function to sort dates in DD/MM/YYYY format.
function sortDates(a, b) {
  const [dA, mA, yA] = a.split("/").map(Number);
  const [dB, mB, yB] = b.split("/").map(Number);
  return new Date(yA, mA - 1, dA) - new Date(yB, mB - 1, dB);
}

// Predefined weekday order for sorting day headers.
const WEEKDAY_ORDER = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];
function sortDays(days) {
  return days.sort((a, b) => {
    const indexA = WEEKDAY_ORDER.indexOf(a);
    const indexB = WEEKDAY_ORDER.indexOf(b);
    if (indexA === -1 && indexB === -1) return a.localeCompare(b);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });
}

export default function PennantDraws() {
  // Final structure: { seasonName: { pennantDay: { [pId]: { displayName, weeks } } } }
  const [drawsData, setDrawsData] = useState({});
  // datesMap: { [pId]: sorted array of week dates }
  const [datesMap, setDatesMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedSeasons, setExpandedSeasons] = useState({});
  const [expandedDays, setExpandedDays] = useState({}); // key: `${seasonName}-${pennantDay}`
  const [expandedPennants, setExpandedPennants] = useState({});

  useEffect(() => {
    async function fetchData() {
      try {
        // 1. Fetch all pennant docs from "Pennants"
        const pennantSnap = await getDocs(collection(db, "Pennants"));
        const pennants = [];
        pennantSnap.forEach((docSnap) => {
          const data = docSnap.data();
          pennants.push({ id: docSnap.id, ...data });
        });
        console.log("Fetched pennants:", pennants);

        // We'll build:
        // - drawsByPennant: { [pId]: { weeks: { [date]: { [section]: [match, ...] } } } }
        // - localDates: { [pId]: Set of week dates }
        const drawsByPennant = {};
        const localDates = {};

        // 2. For each pennant, fetch its draws subcollection.
        await Promise.all(
          pennants.map(async (p) => {
            const pId = p.id;
            drawsByPennant[pId] = { weeks: {} };
            localDates[pId] = new Set();
            const drawsSnap = await getDocs(collection(db, "Pennants", pId, "draws"));
            console.log(`Pennant ${pId} draws:`);
            drawsSnap.forEach((drDoc) => {
              const drData = drDoc.data();
              console.log(`  Draw doc ${drDoc.id}:`, drData);
              if (!Array.isArray(drData.weeks)) return;
              drData.weeks.forEach((wk) => {
                if (wk.date && Array.isArray(wk.matches)) {
                  let grouped;
                  // If the week document has a top-level section, use it.
                  if (wk.section) {
                    grouped = { [wk.section]: wk.matches };
                  } else {
                    // Otherwise, group each match by its own section (defaulting to "1")
                    grouped = wk.matches.reduce((acc, match) => {
                      const section = match.section || match.team1?.section || match.team2?.section || "1";
                      if (!acc[section]) acc[section] = [];
                      acc[section].push(match);
                      return acc;
                    }, {});
                  }
                  // Merge grouped matches into the week date for this pennant.
                  if (!drawsByPennant[pId].weeks[wk.date]) {
                    drawsByPennant[pId].weeks[wk.date] = grouped;
                  } else {
                    Object.keys(grouped).forEach((sec) => {
                      if (!drawsByPennant[pId].weeks[wk.date][sec]) {
                        drawsByPennant[pId].weeks[wk.date][sec] = [];
                      }
                      drawsByPennant[pId].weeks[wk.date][sec] =
                        drawsByPennant[pId].weeks[wk.date][sec].concat(grouped[sec]);
                    });
                  }
                  localDates[pId].add(wk.date);
                }
              });
            });
          })
        );
        console.log("Merged drawsByPennant:", drawsByPennant);

        // 3. Convert each localDates[pId] to a sorted array.
        const finalDatesMap = {};
        Object.keys(localDates).forEach((pId) => {
          const arr = Array.from(localDates[pId]).sort(sortDates);
          finalDatesMap[pId] = arr;
        });
        console.log("Final datesMap:", finalDatesMap);
        setDatesMap(finalDatesMap);

        // 4. Build the final structure grouped by season and pennantDay.
        // Structure: { seasonName: { pennantDay: { [pId]: { displayName, weeks } } } }
        const finalStructure = {};
        pennants.forEach((p) => {
          const { id: pId, seasonName, pennantDay, sport, name } = p;
          const season = seasonName || "Unknown Season";
          const day = pennantDay || "Unknown Day";
          const displayName = (sport ? `${sport} - ` : "") + (name || pId);
          if (!finalStructure[season]) {
            finalStructure[season] = {};
          }
          if (!finalStructure[season][day]) {
            finalStructure[season][day] = {};
          }
          finalStructure[season][day][pId] = {
            displayName,
            weeks: drawsByPennant[pId]?.weeks || {},
          };
        });
        console.log("Final regrouped structure:", finalStructure);
        setDrawsData(finalStructure);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching pennant draws data:", err);
      }
    }
    fetchData();
  }, []);

  // Toggle functions for accordions.
  const toggleSeasonExpand = (season) => {
    setExpandedSeasons((prev) => ({
      ...prev,
      [season]: !prev[season],
    }));
  };

  const toggleDayExpand = (season, day) => {
    const key = `${season}-${day}`;
    setExpandedDays((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const togglePennantExpand = (pId) => {
    setExpandedPennants((prev) => ({
      ...prev,
      [pId]: !prev[pId],
    }));
  };

  if (loading) {
    return <Typography>Loading Draws...</Typography>;
  }

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="h4" gutterBottom>
        Pennant Draws
      </Typography>
      {Object.entries(drawsData).map(([season, daysObj]) => (
        <Box key={season} sx={{ marginBottom: 4 }}>
          {/* Season Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              cursor: "pointer",
              bgcolor: "#e0e0e0",
              p: 1,
            }}
            onClick={() => toggleSeasonExpand(season)}
          >
            <Typography variant="h5">Season: {season}</Typography>
            <IconButton>
              {expandedSeasons[season] ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          {expandedSeasons[season] &&
            sortDays(Object.keys(daysObj)).map((day) => {
              const pennants = daysObj[day];
              const dayKey = `${season}-${day}`;
              return (
                <Box key={day} sx={{ marginBottom: 2, pl: 2 }}>
                  {/* Pennant Day Header */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "pointer",
                      bgcolor: "#f0f0f0",
                      p: 1,
                    }}
                    onClick={() => toggleDayExpand(season, day)}
                  >
                    <Typography variant="h6">{day}</Typography>
                    <IconButton>
                      {expandedDays[dayKey] ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Box>
                  {expandedDays[dayKey] &&
                    Object.entries(pennants).map(([pId, { displayName, weeks }]) => {
                      const weekDates = datesMap[pId] || [];
                      return (
                        <Box key={pId} sx={{ marginBottom: 2, pl: 2 }}>
                          {/* Pennant Header */}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              cursor: "pointer",
                              bgcolor: "#f9f9f9",
                              p: 1,
                            }}
                            onClick={() => togglePennantExpand(pId)}
                          >
                            <Typography variant="h6">{displayName}</Typography>
                            <IconButton>
                              {expandedPennants[pId] ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                          </Box>
                          {expandedPennants[pId] && (
                            <>
                              {weekDates.map((dateStr) => {
                                // For each week date, get the sections grouping.
                                const sections = weeks[dateStr] || {};
                                // Sorted section keys (numeric sort)
                                const sectionKeys = Object.keys(sections).sort(
                                  (a, b) => Number(a) - Number(b)
                                );
                                return (
                                  <Box key={dateStr} sx={{ mb: 2, pl: 3 }}>
                                    <Typography variant="subtitle1" gutterBottom>
                                      {dateStr}
                                    </Typography>
                                    <Box sx={{ display: "flex", gap: 2 }}>
                                      {sectionKeys.map((sec) => (
                                        <Box key={sec} sx={{ flex: 1 }}>
                                          <Typography
                                            variant="subtitle2"
                                            sx={{
                                              fontWeight: "bold",
                                              mb: 1,
                                              bgcolor: "#d0d0ff",
                                              p: 0.5,
                                              borderRadius: 1,
                                            }}
                                          >
                                            Section: {sec}
                                          </Typography>
                                          {sections[sec].map((match, idx) => {
                                            const team1Name = match.team1?.name || "Player1";
                                            const team2Name = match.team2?.name || "Player2";
                                            return (
                                              <Typography key={idx} variant="body2" sx={{ ml: 0.5 }}>
                                                {team1Name} vs {team2Name}
                                              </Typography>
                                            );
                                          })}
                                        </Box>
                                      ))}
                                    </Box>
                                  </Box>
                                );
                              })}
                              {/* Render the PennantTeams component only once per pennant */}
                              <Box sx={{ mt: 2, pl: 2 }}>
                                <PennantTeams pennantId={pId} />
                              </Box>
                            </>
                          )}
                        </Box>
                      );
                    })}
                </Box>
              );
            })}
        </Box>
      ))}
    </Box>
  );
}
